import { currentLocale } from "../lib/i18n";

/** UI locale for numbers, dates, and currency. Keep this dynamic: modules load
 * before `initLocale()` fixes the embedded app's display language. */
export function uiNumberLocale(): string {
  const locale = currentLocale();
  return locale === "ja" ? "ja-JP" : locale === "en" ? "en-US" : locale === "de" ? "de-DE" : locale;
}

/** Format a numeric value using the currently displayed UI language. */
export function formatNumber(value: number): string {
  return value.toLocaleString(uiNumberLocale());
}

/**
 * 金額を店の通貨で表記する（`¥1,234` / `$12.34` / `£1,234.56`）。
 *
 * 🔴 **通貨は翻訳しない＝店の通貨（`/api/me` の `currency`・発注書は `draft.currency`）
 * で出す。** 表示言語（UI ロケール）が決めるのは桁区切りと記号の置き方だけ。日本の店の
 * オーナーが英語UIで見ても金額は ¥ のまま＝仕入先へ出す書類と一致する。
 *
 * ⚠️ **小数桁は通貨が決める**（JPY=0 / USD=2）。旧 `yen()` は全通貨を円未満四捨五入
 * していたので、ドル建ての店ではセントが消えていた。
 */
export function money(amount: string | number, currency: string): string {
  const n = typeof amount === "string" ? Number(amount) : amount;
  const value = Number.isFinite(n) ? n : 0;
  // 🔴 モジュール直下の const で解決しない＝`initLocale()` より前に評価されて
  //    locale が効かない（英語UI 実装で繰り返し踏んだ罠）。呼ばれるたびに引く。
  const locale = uiNumberLocale();
  try {
    // `currencyDisplay` は既定の "symbol" のまま＝"narrowSymbol" にすると
    // AUD も CAD も "$" になり、豪ドルと加ドルが見分けられなくなる。
    const text = new Intl.NumberFormat(locale, { style: "currency", currency }).format(value);
    return normalizeYenSign(text);
  } catch {
    // 未知・不正な通貨コードでも金額を消さない（`Intl` は RangeError を投げる）。
    return `${currency} ${value.toLocaleString(locale)}`;
  }
}

/**
 * 🔴 `Intl` は ja-JP × JPY で**全角 ￥（U+FFE5）**を返す（en-US では半角 ¥ = U+00A5）。
 * 旧 `yen()` は半角だったので、そのまま採用すると**既存の日本の店の金額表示が
 * 画面・PDF・CSV で一斉に変わる**（実ユーザーは 100% 日本の店）。半角へ寄せて
 * 現行の見た目を維持する。
 *
 * ⚠️ 正負で1点だけ挙動が変わる＝旧 `¥-1,234` → 新 `-¥1,235`（記号の位置と、
 * 0.5 の丸め方向）。発注書・在庫評価額はいずれも非負なので実害は無く、旧実装の
 * `¥-0` のほうが表示として壊れていた。
 */
function normalizeYenSign(text: string): string {
  return text.replace(/￥/g, "¥");
}

/** 通貨未取得の面で使う既定。backend の `currency.DEFAULT_CURRENCY` と揃える。 */
export const DEFAULT_CURRENCY = "JPY";

/**
 * 金額入力欄の接頭辞にする通貨記号だけ（`¥` / `$` / `£`）。
 *
 * `money()` は桁区切りまで付けるので、入力欄の prefix には使えない。`Intl` の
 * `formatToParts` から記号の部分だけを取り出す＝記号表を自前で持たない。
 */
export function currencySymbol(currency: string): string {
  try {
    const parts = new Intl.NumberFormat(uiNumberLocale(), {
      style: "currency",
      currency,
    }).formatToParts(0);
    const symbol = parts.find((part) => part.type === "currency")?.value ?? currency;
    return normalizeYenSign(symbol);
  } catch {
    return currency;
  }
}

/**
 * その通貨の小数桁数（JPY=0 / USD=2 / KWD=3）。backend の `currency.minor_units`
 * と同じ答えを返す必要があるが、桁数は `Intl` が知っているので表を持たない。
 */
export function minorUnits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/**
 * 金額**入力欄**（TextField）の表示値。API の金額文字列は Numeric(12,2) 由来で常に
 * 小数2桁（"720.00"）なので、JPY のような小数を持たない通貨ではそのまま出すと
 * 「¥ 720.00」になる。通貨の小数桁が 0 で、かつ値が整数のときだけ小数部を落とす
 * （"720.00" → "720"）。USD 等の小数を持つ通貨は無変換。
 *
 * ⚠️ 整数でない値はそのまま返す＝表示のために数字を丸めて嘘をつかない
 * （backend が JPY を整数へ量子化しているので、実データでは起きない防御）。
 */
export function moneyInputValue(value: string, currency: string): string {
  if (minorUnits(currency) !== 0) return value;
  if (value.trim() === "") return value; // Number("") は 0 になる＝空欄を "0" にしない
  const n = Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return value;
  return String(n);
}

/**
 * CSV の金額列用。**記号を付けず数値で返す**（表計算で集計できるようにする）。
 * 🔴 丸めは通貨の最小単位で行う＝旧 `roundYen` は全通貨を整数に丸めていたので、
 * ドル建ての店ではセントが落ちていた。
 */
export function roundMoney(amount: string | number, currency: string): number {
  const n = typeof amount === "string" ? Number(amount) : amount;
  if (!Number.isFinite(n)) return 0;
  const factor = 10 ** minorUnits(currency);
  return Math.round(n * factor) / factor;
}
